import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import type {
  SpeechRecognitionInstance,
  SpeechRecognitionEvent,
  SpeechRecognitionErrorEvent,
} from '../types/speech-recognition';

export interface UseSpeechRecognitionOptions {
  lang?: string;
  continuous?: boolean;
  interimResults?: boolean;
  onTranscript?: (transcript: string) => void;
  onError?: (error: string) => void;
}

export function useSpeechRecognition({
  lang = 'es-CO',
  continuous = false,
  interimResults = false,
  onTranscript,
  onError,
}: UseSpeechRecognitionOptions = {}) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);

  useEffect(() => {
    const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
    setIsSupported(Boolean(SpeechRec));
  }, []);

  const triggerHaptic = (duration = 30) => {
    try {
      if (typeof window !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate(duration);
      }
    } catch {
      // Haptics not allowed or not supported
    }
  };

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // Ignored
      }
    }
    setIsListening(false);
  }, []);

  const startListening = useCallback(() => {
    const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRec) {
      toast.info('El reconocimiento de voz no está soportado en este navegador.');
      return;
    }

    try {
      // If an existing instance is running, abort it first
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {
          // ignore
        }
      }

      const instance = new SpeechRec();
      instance.lang = lang;
      instance.continuous = continuous;
      instance.interimResults = interimResults;
      instance.maxAlternatives = 1;

      instance.onstart = () => {
        setIsListening(true);
        triggerHaptic(40);
      };

      instance.onresult = (event: SpeechRecognitionEvent) => {
        const lastIndex = event.resultIndex;
        const text = event.results[lastIndex][0]?.transcript?.trim() || '';
        if (text) {
          setTranscript(text);
          onTranscript?.(text);
        }
      };

      instance.onerror = (event: SpeechRecognitionErrorEvent) => {
        setIsListening(false);
        let errorMsg = 'Error en reconocimiento de voz';

        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          errorMsg = 'Permiso de micrófono no concedido.';
          toast.error(errorMsg);
        } else if (event.error === 'no-speech') {
          // Normal when user didn't speak in time
          return;
        } else if (event.error === 'network') {
          errorMsg = 'Error de conexión de red para voz.';
          toast.warn(errorMsg);
        }

        onError?.(errorMsg);
      };

      instance.onend = () => {
        setIsListening(false);
        triggerHaptic(20);
      };

      recognitionRef.current = instance;
      instance.start();
    } catch (err: any) {
      console.warn('SpeechRecognition start failed:', err);
      setIsListening(false);
    }
  }, [lang, continuous, interimResults, onTranscript, onError]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  // Clean up when unmounting
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {
          // ignore
        }
      }
    };
  }, []);

  return {
    isListening,
    transcript,
    isSupported,
    startListening,
    stopListening,
    toggleListening,
  };
}
